import 'tinymce/tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/skins/ui/oxide/skin';
import 'tinymce/skins/ui/oxide/content';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/autosave';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/code';
// import 'tinymce/plugins/codesample';
// import 'tinymce/plugins/directionality';
// import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/help';
import 'tinymce/plugins/help/js/i18n/keynav/en';
import 'tinymce/plugins/image';
import 'tinymce/plugins/importcss';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/media';
import 'tinymce/plugins/nonbreaking';
import 'tinymce/plugins/pagebreak';
import 'tinymce/plugins/preview';
// import 'tinymce/plugins/quickbars';
import 'tinymce/plugins/save';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/table';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/visualchars';
import { TextField, type TextFieldProps } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useState } from 'react';
import { useController, useFormContext, type RegisterOptions } from 'react-hook-form';
import { cleanWordPasteHtml } from './cleanWordPasteHtml.ts';
// import 'tinymce/plugins/wordcount';

type RichTextField3Props = {
  name: string;
  rules?: RegisterOptions;
  height?: number;
  onImageUpload?: (file: File) => Promise<string> | string;
} & Omit<TextFieldProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'inputRef'>;

export const RichTextField3 = ({
  name,
  rules,
  onImageUpload,
  height,
  ...props
}: RichTextField3Props) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
    rules,
  });

  const [initialValue, setInitialValue] = useState(field.value ?? '');
  const [isUserInput, setIsUserInput] = useState(false);
  if (!isUserInput && !initialValue && field.value) {
    setInitialValue(field.value);
  }

  return (
    <TextField
      margin="normal"
      variant="outlined"
      {...props}
      ref={field.ref}
      error={!!fieldState.error}
      helperText={fieldState.error?.message}
      slotProps={{
        inputLabel: { shrink: true },
        htmlInput: {
          style: { padding: 12, width: '100%', height: '100%' },
          children: (
            <Editor
              licenseKey="gpl"
              initialValue={initialValue}
              onEditorChange={(content) => {
                setIsUserInput(true);
                field.onChange(content);
              }}
              init={{
                // menubar: true,
                min_height: 250,
                max_height: 1500,
                xss_sanitization: true,
                relative_urls: false,
                images_upload_base_path: '/',
                images_upload_credentials: true,
                async images_upload_handler(blobInfo, _progress) {
                  if (!onImageUpload) {
                    throw new Error('画像は利用できません');
                  }

                  const blob = blobInfo.blob();
                  const file = new File([blob], blobInfo.name(), { type: blob.type });
                  const src = await onImageUpload(file);

                  return src;
                },
                language: 'ja',
                language_url: '/public/tinymce/langs/ja.js',
                setup: (editor) => {
                  editor.on('ScriptsLoaded', () => {
                    editor.editorManager.addI18n('ja', {
                      'Insert/edit media': '動画',
                      Source: 'URL',
                      Embed: 'ソース',
                      'Paste your embed code below:': '',
                    });
                  });
                },
                resize: true,
                object_resizing: true,
                smart_paste: true,
                paste_postprocess: (_editor, args) => {
                  args.node.innerHTML = cleanWordPasteHtml(args.node.innerHTML);
                },
                plugins: [
                  'advlist',
                  'anchor',
                  'autolink',
                  'autoresize',
                  'autosave',
                  'charmap',
                  'code',
                  'fullscreen',
                  'help',
                  'help/js/i18n/keynav/ja',
                  'image',
                  'importcss',
                  'insertdatetime',
                  'link',
                  'lists',
                  'nonbreaking',
                  'pagebreak',
                  'preview',
                  'save',
                  'searchreplace',
                  'table',
                  'visualblocks',
                  'visualchars',
                  'media',
                ].filter(Boolean),
                toolbar:
                  'media | undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | outdent indent',
                content_style:
                  'body { font-family: Helvetica,Arial,sans-serif; font-size:14px } table { border: 1px solid #999; border-collapse: collapse; } table td, table th { border: 1px solid #999; }',
                media_dimensions: false,
                media_alt_source: false,
                media_poster: false,
                iframe_template_callback: (data: { source: string; allowfullscreen?: string }) => {
                  const allowFullscreen = data.allowfullscreen === 'true' ? ' allowfullscreen' : '';
                  return `<iframe src="${data.source}" style="width: 100%; height: auto; aspect-ratio: 16 / 9; max-width: 740px;"${allowFullscreen}></iframe>`;
                },
                promotion: false,
                branding: false,
                help_accessibility: false,
              }}
            />
          ),
        },
      }}
      slots={{
        htmlInput: 'div',
      }}
    />
  );
};
